/**
 * Shipping Addresses Modal
 * 
 * Manage multiple shipping addresses for a customer
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import AddressEditModal from './AddressEditModal';

interface Address {
  id?: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  is_default?: boolean;
}

interface ShippingAddressesModalProps {
  open: boolean;
  onClose: () => void;
  partyName: string;
  addresses: Address[];
  onAdd: (address: Address) => Promise<void>;
  onEdit: (addressId: string, address: Address) => Promise<void>;
  onDelete: (addressId: string) => Promise<void>;
  onSetDefault: (addressId: string) => Promise<void>;
}

const ShippingAddressesModal: React.FC<ShippingAddressesModalProps> = ({
  open,
  onClose,
  partyName,
  addresses,
  onAdd,
  onEdit,
  onDelete,
  onSetDefault,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | undefined>(undefined);
  const [loading, setLoading] = useState<string | null>(null);

  const formatAddress = (address: Address) => {
    const parts = [
      address.address_line1,
      address.address_line2,
      address.city,
      address.state,
      address.pincode,
    ].filter(Boolean);
    return parts.join(', ');
  };

  const handleAddNew = () => {
    setEditingAddress(undefined);
    setEditModalOpen(true);
  };

  const handleEdit = (address: Address) => {
    setEditingAddress(address);
    setEditModalOpen(true);
  };

  const handleSaveAddress = async (address: Address) => {
    try {
      if (editingAddress?.id) {
        await onEdit(editingAddress.id, address);
        enqueueSnackbar('Address updated successfully', { variant: 'success' });
      } else {
        await onAdd(address);
        enqueueSnackbar('Address added successfully', { variant: 'success' });
      }
      setEditModalOpen(false);
    } catch (error: any) {
      throw error; // Let AddressEditModal handle the error
    }
  };

  const handleDelete = async (addressId: string) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    setLoading(`delete-${addressId}`);
    try {
      await onDelete(addressId);
      enqueueSnackbar('Address deleted successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Error deleting address:', error);
      enqueueSnackbar(error.message || 'Failed to delete address', { variant: 'error' });
    } finally {
      setLoading(null);
    }
  };

  const handleSetDefault = async (addressId: string) => {
    setLoading(`default-${addressId}`);
    try {
      await onSetDefault(addressId);
      enqueueSnackbar('Default address updated', { variant: 'success' });
    } catch (error: any) {
      console.error('Error setting default:', error);
      enqueueSnackbar(error.message || 'Failed to set default address', { variant: 'error' });
    } finally {
      setLoading(null);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(90deg, #2196f3 0%, #0d47a1 100%)',
            color: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Shipping Addresses
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {partyName}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {addresses.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No shipping addresses configured. Click "Add Address" to create one.
            </Alert>
          ) : (
            <List sx={{ p: 0 }}>
              {addresses.map((address, index) => (
                <React.Fragment key={address.id || index}>
                  {index > 0 && <Divider />}
                  <ListItem
                    sx={{
                      py: 2,
                      px: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(33, 150, 243, 0.04)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Address {index + 1}
                          </Typography>
                          {address.is_default && (
                            <Chip
                              label="Default"
                              size="small"
                              color="primary"
                              icon={<StarIcon />}
                              sx={{ borderRadius: 4, height: 24 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {formatAddress(address)}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {!address.is_default && (
                          <IconButton
                            size="small"
                            onClick={() => address.id && handleSetDefault(address.id)}
                            disabled={loading === `default-${address.id}`}
                            title="Set as default"
                          >
                            <StarBorderIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(address)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => address.id && handleDelete(address.id)}
                          disabled={loading === `delete-${address.id}`}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleAddNew}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Add Address
          </Button>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Edit Modal */}
      <AddressEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        address={editingAddress}
        addressType="shipping"
        partyName={partyName}
        onSave={handleSaveAddress}
      />
    </>
  );
};

export default ShippingAddressesModal;
